import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../components/ui/Button';
import { BarChart2Icon, UsersIcon, LineChartIcon, LayoutIcon, BriefcaseIcon, SearchIcon, CheckIcon } from 'lucide-react';
const Services = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const services = [{
    icon: <UsersIcon size={36} data-id="element-607" />,
    title: 'Consumer & Lifestyle Insights',
    description: "Uncover the behaviors, preferences, and trends that drive consumer decisions in Toronto's diverse markets.",
    features: ['Demographic profiling and segmentation', 'Consumer behavior analysis', 'Trend forecasting and monitoring', 'Lifestyle and values research']
  }, {
    icon: <LayoutIcon size={36} data-id="element-608" />,
    title: 'Brand Perception & Ad Testing',
    description: 'Optimize your brand messaging and advertising effectiveness through rigorous testing methodologies.',
    features: ['Brand health tracking', 'Competitive positioning analysis', 'Ad concept and message testing', 'Cross-cultural brand perception studies']
  }, {
    icon: <SearchIcon size={36} data-id="element-609" />,
    title: 'UX & Product Research',
    description: 'Design better digital experiences with user testing and product research tailored to your target audience.',
    features: ['Usability testing and evaluation', 'User journey mapping', 'Product concept testing', 'Competitive product analysis']
  }, {
    icon: <LineChartIcon size={36} data-id="element-610" />,
    title: 'Urban Planning & Infrastructure',
    description: 'Support smart city initiatives with data-driven insights on urban mobility, housing, and public services.',
    features: ['Transit and mobility studies', 'Housing preferences and trends', 'Public space utilization research', 'Smart city technology adoption']
  }, {
    icon: <BriefcaseIcon size={36} data-id="element-611" />,
    title: 'Sports & Entertainment Trends',
    description: "Tap into Toronto's vibrant sports and entertainment culture with specialized audience research.",
    features: ['Fan engagement and loyalty studies', 'Event experience optimization', 'Sponsorship effectiveness measurement', 'Cultural participation and preferences']
  }, {
    icon: <BarChart2Icon size={36} data-id="element-612" />,
    title: 'Qualitative & Quantitative Research',
    description: 'Comprehensive research solutions combining in-depth interviews, focus groups, surveys, and statistical analysis.',
    features: ['Focus groups and in-depth interviews', 'Quantitative surveys and polling', 'Ethnographic research', 'Mixed-method research design']
  }];
  return <div className="pt-20" data-id="element-613">
      {/* Hero Section */}
      <section className="bg-neutral-light py-20" data-id="element-614">
        <div className="container mx-auto px-4 md:px-6" data-id="element-615">
          <div className="max-w-3xl mx-auto text-center" data-id="element-616">
            <motion.h1 initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="text-4xl md:text-5xl font-display font-bold mb-6" data-id="element-617">
              Our <span className="text-toronto-blue" data-id="element-618">Services</span>
            </motion.h1>
            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.2,
            duration: 0.5
          }} className="text-xl text-neutral-dark/80" data-id="element-619">
              Comprehensive research solutions tailored to Toronto's unique
              multicultural landscape.
            </motion.p>
          </div>
        </div>
      </section>
      {/* Services Section */}
      <section className="py-16 bg-white" data-id="element-620">
        <div className="container mx-auto px-4 md:px-6" data-id="element-621">
          <div className="grid grid-cols-1 gap-16" data-id="element-622">
            {services.map((service, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className={`grid grid-cols-1 md:grid-cols-2 gap-8 items-center ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''}`} data-id="element-623">
                <div className={`order-2 ${index % 2 !== 0 ? 'md:order-1' : 'md:order-2'}`} data-id="element-624">
                  <div className="bg-toronto-blue/10 text-toronto-blue p-4 rounded-full w-16 h-16 flex items-center justify-center mb-4" data-id="element-625">
                    {service.icon}
                  </div>
                  <h2 className="text-3xl font-display font-bold mb-4" data-id="element-626">
                    {service.title}
                  </h2>
                  <p className="text-lg text-neutral-dark/80 mb-6" data-id="element-627">
                    {service.description}
                  </p>
                  <ul className="space-y-3 mb-8" data-id="element-628">
                    {service.features.map((feature, i) => <li key={i} className="flex items-start" data-id="element-629">
                        <CheckIcon size={18} className="text-toronto-blue mr-2 mt-1 flex-shrink-0" data-id="element-630" />
                        <span data-id="element-631">{feature}</span>
                      </li>)}
                  </ul>
                  <Button variant="primary" data-id="element-632">Learn More</Button>
                </div>
                <div className={`order-1 ${index % 2 !== 0 ? 'md:order-2' : 'md:order-1'}`} data-id="element-633">
                  <div className="bg-neutral-light h-64 md:h-80 rounded-lg shadow-md flex items-center justify-center p-8" data-id="element-634">
                    <img src={`https://source.unsplash.com/random/600x400/?${service.title.toLowerCase().replace(/&|\s/g, '-')}`} alt={service.title} className="max-h-full w-auto rounded" data-id="element-635" />
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* Methodology Section */}
      <section className="py-16 bg-neutral-light/50" data-id="element-636">
        <div className="container mx-auto px-4 md:px-6" data-id="element-637">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-638">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4" data-id="element-639">
              Our Research Methodology
            </h2>
            <p className="text-lg text-neutral-dark/80" data-id="element-640">
              We combine proven research techniques with innovative approaches
              to deliver actionable insights.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-641">
            {[{
            title: 'Discovery',
            description: 'We begin by understanding your objectives, challenges, and the decisions you need to make.'
          }, {
            title: 'Design',
            description: 'Our team creates a custom research plan using the most appropriate methodologies for your needs.'
          }, {
            title: 'Data Collection',
            description: 'We gather insights through surveys, interviews, focus groups, and other specialized techniques.'
          }, {
            title: 'Delivery',
            description: 'We analyze the data and deliver actionable insights with clear recommendations.'
          }].map((step, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.5
          }} className="bg-white p-6 rounded-lg shadow-md relative" data-id="element-642">
                <div className="absolute top-0 left-0 w-full h-1 bg-toronto-blue" data-id="element-643" />
                <div className="bg-toronto-blue text-white w-8 h-8 rounded-full flex items-center justify-center font-bold mb-4" data-id="element-644">
                  {index + 1}
                </div>
                <h3 className="text-xl font-semibold mb-2" data-id="element-645">{step.title}</h3>
                <p className="text-neutral-dark/80" data-id="element-646">{step.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-toronto-blue text-white" data-id="element-647">
        <div className="container mx-auto px-4 md:px-6" data-id="element-648">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center" data-id="element-649">
            <div data-id="element-650">
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-6" data-id="element-651">
                Ready to Start Your Research Project?
              </h2>
              <p className="text-lg mb-8 opacity-90" data-id="element-652">
                Contact our team to discuss your research needs and discover how
                Market Research Toronto can help you gain the insights you need.
              </p>
              <Button variant="secondary" size="lg" data-id="element-653">
                Schedule a Consultation
              </Button>
            </div>
            <div className="bg-white/10 p-6 rounded-lg" data-id="element-654">
              <h3 className="text-xl font-semibold mb-4" data-id="element-655">
                Why Choose Market Research Toronto?
              </h3>
              <ul className="space-y-3" data-id="element-656">
                {["Specialized expertise in Toronto's multicultural market", 'Bilingual research capabilities', 'Innovative research methodologies', 'Actionable insights and recommendations', 'Rigorous quality standards', 'Experienced, professional team'].map((item, index) => <li key={index} className="flex items-start" data-id="element-657">
                    <CheckIcon size={18} className="text-canada-red mr-2 mt-1 flex-shrink-0" data-id="element-658" />
                    <span data-id="element-659">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>;
};
export default Services;